import { Alert, Button, Field, Modal, Select, TextArea, useToast } from "@nabble/ui";
import { type FormEvent, useState } from "react";
import { createDataRequest, type DataRequestKind } from "../../../../api/dataRequests";
import { DETAILS_MAX, dataRequestError, detailsRequired, KIND_LABEL, KINDS } from "../../../../model/dataRequests";
import { detailsProblem } from "./detailsProblem";

const KIND_OPTIONS = KINDS.map((k) => ({ value: k, label: KIND_LABEL[k] }));

/** Asks what the person wants done with their data (Law 21.719) and sends the request. */
export function MakeRequestDialog({ onClose, onSent }: { onClose: () => void; onSent: () => void }): React.JSX.Element {
  const { show } = useToast();
  const [kind, setKind] = useState<DataRequestKind>("ACCESS");
  const [details, setDetails] = useState("");
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const required = detailsRequired(kind);

  async function submit(e?: FormEvent): Promise<void> {
    e?.preventDefault();
    setError(null);
    setDetailsError(null);
    const text = details.trim();
    const problem = detailsProblem(kind, text);
    if (problem) {
      setDetailsError(problem);
      return;
    }
    setLoading(true);
    try {
      await createDataRequest(kind, text);
      show({ title: "Request sent", text: "We'll answer within 30 calendar days.", tone: "success" });
      onSent();
    } catch (err) {
      setError(dataRequestError(err, "We couldn't send your request. Please try again."));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open
      icon="shield"
      title="Make a request about your data"
      description="Choose what you'd like us to do with your personal data."
      onClose={onClose}
      actions={
        <>
          <Button label="Cancel" variant="secondary" onClick={onClose} />
          <Button label="Send request" icon="send" loading={loading} onClick={() => void submit()} />
        </>
      }
    >
      <form className="nb-stack" onSubmit={(e) => void submit(e)} noValidate>
        <Field label="What would you like?">
          <Select
            value={kind}
            onValueChange={(v) => {
              setKind(v as DataRequestKind);
              setDetailsError(null);
            }}
            options={KIND_OPTIONS}
          />
        </Field>
        <Field
          label="Details"
          optional={!required}
          error={detailsError ?? undefined}
          help={required ? "Tell us which data is wrong and what it should say." : "Anything that helps us understand your request."}
        >
          <TextArea value={details} onChange={(e) => setDetails(e.target.value)} rows={4} maxLength={DETAILS_MAX} />
        </Field>
        <p className="nb-help" style={{ margin: 0 }}>
          We answer within 30 calendar days.
        </p>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
